import React, { memo, useCallback, useMemo } from 'react';

import { useIsFocused, useNavigation } from '@react-navigation/native';

import { EntryCard, EntryCardHeaderProps, EntryCardPayoutProps, EntryCashOutControls } from '@/components/entry-card';
import { ds } from '@/components/lib/design.system';
import { EntryItemType } from '@/feature/entries-pickem/components/EntriesList';
import { PicksEntryCardBottom } from '@/feature/entries-pickem/components/PicksEntryCardBottom';
import {
    getCardState,
    getEntryMultiplier,
    getEntryResultAmount,
    getEntryScreenStatusBadge,
    isFreeEntry,
} from '@/feature/entries-pickem/entries-utils';
import { useCashOutPolling } from '@/feature/entries-pickem/hooks/use-cash-out';
import { useComputeEntryPromos } from '@/feature/entries-pickem/hooks/use-compute-entry-promos';
import { useEntryAvailableActions } from '@/feature/entries-pickem/hooks/use-entry-available-actions';
import { usePickemEntryCashOut } from '@/feature/entries-pickem/hooks/use-pickem-entry-cash-out';
import { usePickemEntryPromos } from '@/feature/entries-pickem/hooks/use-pickem-entry-promos';
import { getRefundedAmountFromPromo } from '@/hooks/use-entries-utils';
import { PayoutCurrency, Result } from '@/types/api.generated';
import { getEntryPayout } from '@/utils/getEntryPayout';

type Props = { item: EntryItemType; isVisible: boolean };

export const EntriesListItem = memo(({ item, isVisible }: Props) => {
    const { amount, picks, result, id, createdDate, status } = item;
    const navigation = useNavigation();
    const isVoided = result === Result.Void;
    const isLoss = result === Result.Loss;
    const isFocused = useIsFocused();

    const { canCashout } = useEntryAvailableActions(item);
    const cashoutPollingEnabled = isVisible && isFocused && canCashout;
    const cashoutOffer = useCashOutPolling(item, cashoutPollingEnabled);
    const activeCashoutOffer = canCashout ? cashoutOffer ?? null : null;
    const cashOut = usePickemEntryCashOut(item, activeCashoutOffer, 'list');

    const onSharePress = useCallback(() => {
        navigation.navigate('EntryShare', { id });
    }, [id, navigation]);

    const entryPromotions = useComputeEntryPromos(item);
    const isEntryPlacedWithBetrBucks = entryPromotions.some(promo => promo.id === 'betrBucks');
    const hasRefundedAmountOnPromo = getRefundedAmountFromPromo(item) !== null;
    const isBnsPayoutCurrency = item.payoutCurrency === PayoutCurrency.Bns;
    const payoutTag = hasRefundedAmountOnPromo ? 'Betr Bucks' : isBnsPayoutCurrency ? 'Play-Through' : undefined;
    const entryPayout = getEntryPayout(item);
    const entryResultAmount = getEntryResultAmount(item);
    const entryStatus = getEntryScreenStatusBadge({ isRefunded: isVoided, status, result });
    const playerNames = useMemo(
        () => picks.map(pick => pick.player && (pick.player.lastName || pick.player.firstName)).join(', '),
        [picks]
    );

    const baseHeader: EntryCardHeaderProps = {
        title: `${entryPayout.availableCount}-Pick Lineup`,
        subtitle: playerNames,
        trailing: getEntryMultiplier(item),
        hideMultiplier: isFreeEntry(item),
        trailingColor: ds.colorToken.text.brand,
        status: entryStatus,
    };

    const basePayout: EntryCardPayoutProps = {
        entryLabel: 'Entry',
        entryAmount: `$${amount}`,
        payoutTag,
        payoutTagBackgroundColor: isBnsPayoutCurrency ? ds.colorToken.background.neutral.stronger : undefined,
        entryTag: isEntryPlacedWithBetrBucks ? 'Betr Bucks' : undefined,
        payoutLabel: 'Payout',
        payoutAmount: entryResultAmount,
        payoutColor: result && !isLoss ? ds.colorToken.text.primary : 'neutral300',
    };

    // The cash-out phase drives the header badge and payout column; the bottom is unchanged.
    const header = { ...baseHeader, status: cashOut.status ?? entryStatus };
    const payout = { ...basePayout, ...cashOut.payout };

    const cardState = useMemo(() => getCardState(picks, createdDate) ?? '', [picks, createdDate]);

    const promos = usePickemEntryPromos(entryPromotions, result);

    const onPressEntryCard = useCallback(() => {
        navigation.navigate('EntryScreen', { id });
    }, [id, navigation]);

    return (
        <EntryCard
            variant={'pickem'}
            onPress={onPressEntryCard}
            header={header}
            payout={payout}
            promos={promos}
            cashOut={cashOut.controls ? <EntryCashOutControls {...cashOut.controls} /> : undefined}
            bottom={
                <PicksEntryCardBottom
                    picks={picks}
                    label={cardState}
                    onSharePress={onSharePress}
                    onCardPress={onPressEntryCard}
                />
            }
        />
    );
});

EntriesListItem.displayName = 'EntriesListItem';
